import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './AuthContext';
import { useTheme } from './ThemeContext';
import '../styles/Navbar.css';

const Navbar = ({ lang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const userRoles = user ? (Array.isArray(user.role) ? user.role : [user.role]) : [];
  const userPos = user?.position;
  const isKyThuat = userRoles.includes('ky_thuat');
  const hasAdminPower = isKyThuat
    || (userRoles.includes('ban_dieu_hanh') && userPos === 'culi_cap_cao')
    || (userRoles.includes('ban_su_kien') && (userPos === 'tap_su' || userPos === 'culi_cap_cao'))
    || userRoles.includes('ban_dao_tao');
  const { isDark, toggleTheme } = useTheme();
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const [recruitOpen, setRecruitOpen] = useState(false);

  useEffect(() => {
    fetch('/api/recruitment')
      .then(r => r.json())
      .then(d => setRecruitOpen(!!d.isOpen))
      .catch(() => {});
  }, []);

  const memberPaths = new Set([
    '/dashboard', '/profile-settings', '/security', '/qa', '/training-area',
    '/ctf', '/resources', '/calendar', '/nomination'
  ]);
  const isMemberArea = memberPaths.has(location.pathname);
  const isAdminArea = location.pathname === '/setting' || /manager\/?$/.test(location.pathname);
  const isDashboard = isMemberArea || isAdminArea;

  // Public pages keep their navigation for both guests and signed-in users.
  // Hide it only inside member/admin workspaces where the sidebar takes over.
  const navItems = isDashboard ? [] : [
    { key: 'home', label: lang.NAV_HOME || 'Home', path: '/home' },
    { key: 'about', label: lang.NAV_ABOUT || 'About', path: '/about' },
    { key: 'activities', label: lang.NAV_ACTIVITIES || 'Activities', path: '/activities' },
    { key: 'achievements', label: lang.NAV_ACHIEVEMENTS || 'Achievements', path: '/achievements' },
    { key: 'members', label: lang.NAV_MEMBERS || 'Members', path: '/members' },
    { key: 'blog', label: lang.NAV_BLOG || 'Blog', path: '/blog' },
    { key: 'events', label: lang.NAV_EVENTS || 'Events', path: '/events' },
    { key: 'contribute', label: lang.NAV_CONTRIBUTE || 'Contribute', path: '/contribute' },
    ...(recruitOpen ? [{ key: 'recruitment', label: lang.NAV_RECRUITMENT || 'Recruitment', path: '/recruitment' }] : []),
  ];

  const handleLogout = () => {
    setUserMenuOpen(false);
    logout();
    navigate('/home');
  };

  return (
    <motion.nav
      className={`navbar ${scrolled ? 'navbar-scrolled' : ''} ${isDashboard ? 'navbar-dashboard' : ''} ${isMemberArea ? 'navbar-member-area' : ''} ${isAuthenticated ? 'navbar-authenticated' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
    >
      <div className="navbar-container">
        <Link to="/home" className="navbar-logo">
          <img src="/logo.png" alt="ISP Club" className="navbar-logo-img" />
        </Link>

        <div className="navbar-links">
          {navItems.map((item) => (
            <Link
              key={item.key}
              to={item.path}
              className={`nav-link ${location.pathname === item.path ? 'active' : ''}`}
            >
              <span className="nav-link-text">{item.label}</span>
              <span className="nav-link-underline"></span>
            </Link>
          ))}
        </div>

        <div className="navbar-actions">
          <button className="theme-toggle" onClick={toggleTheme} aria-label={lang.NAV_TOGGLE_THEME || 'Toggle theme'}>
            {isDark ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            )}
          </button>

          {isAuthenticated ? (
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button
                className={`btn btn-outline navbar-cta user-menu-trigger ${userMenuOpen ? 'active' : ''}`}
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                {user?.avatar ? (
                  <img
                    src={`/uploads/profiles/${user.avatar}`}
                    alt=""
                    className="user-menu-avatar"
                  />
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                )}
                {user?.username || 'User'}
                <svg className={`chevron ${userMenuOpen ? 'open' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>

              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div
                    className="user-dropdown"
                    initial={{ opacity: 0, y: -8, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                  >
                    <div className="user-dropdown-header">
                      <span className="user-dropdown-name">{user?.fullName}</span>
                      <div className="user-dropdown-roles">
                        {(Array.isArray(user?.role) ? user.role : []).map((r, i) => (
                          <span key={i} className={`user-role-badge role-${r}`}>
                            {r === 'ban_dieu_hanh' ? 'Ban Điều Hành'
                              : r === 'ky_thuat' ? 'Kỹ Thuật'
                              : r === 'thanh_vien' ? 'Thành Viên'
                              : r === 'ban_su_kien' ? 'Ban Sự Kiện'
                              : r === 'ban_dao_tao' ? 'Ban Đào Tạo' : r}
                          </span>
                        ))}
                      </div>
                      <span className="user-dropdown-email">{user?.email}</span>
                    </div>
                    <div className="user-dropdown-divider"></div>
                    <Link to="/home" className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      {lang.NAV_HOME || 'Home'}
                    </Link>
                    <Link to="/dashboard" className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                        <rect x="3" y="3" width="7" height="9"/><rect x="14" y="3" width="7" height="5"/><rect x="14" y="12" width="7" height="9"/><rect x="3" y="16" width="7" height="5"/>
                      </svg>
                      {lang.NAV_DASHBOARD || 'Dashboard'}
                    </Link>
                    {hasAdminPower && (
                      <Link to="/setting" className="user-dropdown-item" onClick={() => setUserMenuOpen(false)}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                          <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                        </svg>
                        Panel Quản Trị
                      </Link>
                    )}
                    <button className="user-dropdown-item user-dropdown-logout" onClick={handleLogout}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                      </svg>
                      {lang.NAV_LOGOUT || 'Logout'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link to="/login" className="btn btn-outline navbar-cta">
              {lang.NAV_MEMBER_DASHBOARD || 'Member Dashboard'}
            </Link>
          )}
        </div>

        <button
          className="theme-toggle theme-toggle-mobile"
          onClick={toggleTheme}
          aria-label={lang.NAV_TOGGLE_THEME || 'Toggle theme'}
        >
          {isDark ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="18" height="18">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>

        <button
          className={`navbar-hamburger ${mobileMenuOpen ? 'active' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <span></span><span></span><span></span>
        </button>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            {navItems.map((item) => (
              <Link key={item.key} to={item.path} className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                {item.label}
              </Link>
            ))}
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>
                  {lang.NAV_DASHBOARD || 'Dashboard'}
                </Link>
                <button className="mobile-nav-link mobile-logout" onClick={() => { setMobileMenuOpen(false); handleLogout(); }}>
                  {lang.NAV_LOGOUT || 'Logout'}
                </button>
              </>
            ) : (
              <Link to="/login" className="btn btn-outline mobile-cta" onClick={() => setMobileMenuOpen(false)}>
                {lang.NAV_MEMBER_DASHBOARD || 'Member Dashboard'}
              </Link>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
